import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import csiHeading from "csi/heading/_base";
import { C8Node, cnl } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Writer from "csi/xml/Writer";
import _ from "lodash";
import RuleStore from "./RuleStore";

const specBuilder = new SpecBuilder();

const conjunctionMap = {
	RuleOperatorAllId: "AND",
	RuleOperatorAnyId: "OR",
};

/** exclude blank option for these operators */
export const EXCLUDE_BLANK_OPERATOR_LIST: string[] = ["RE", "NM"];

export const OPERATORS_THAT_SHOW_MULTI_SELECT: Set<string> = new Set([xml.OP_CONTAINS, xml.OP_NOT_CONTAINS]);

export const OPERATORS_THAT_SHOW_TEXT_FIELD: Set<string> = new Set([xml.OP_MATCHES, xml.OP_NOT_MATCHES, xml.OP_GREATER_THAN, xml.OP_GREATER_EQUAL, xml.OP_LESS_THAN, xml.OP_LESS_EQUAL]);

export const OPERATORS_NOT_APPLICABLE_TO_RELATIVE_FILTER = new Set([
	xml.OP_CONTAINS,
	xml.OP_NOT_CONTAINS,
	xml.OP_BLANKS + xml.OP_BLANK_DEFAULT,
	xml.OP_NON_BLANKS + xml.OP_BLANK_DEFAULT,
	xml.OP_BLANKS + xml.OP_BLANK_TIME,
	xml.OP_NON_BLANKS + xml.OP_BLANK_TIME,
	xml.OP_NULL,
	xml.OP_NOT_NULL,
	xml.OP_RANGE,
	xml.OP_WITHIN,
]);

export type RuleOnApplyFilterFunction = (valueQuery: Query, spec: Spec, op: string, values: any[]) => void;

export const RULE_FILTER_ALL_ITEMS = "RuleFilterItemsAll";

/**
 * the type for individual rule
 */
export type RuleSchema = {
	/** the index of the attribute - only populated when persisted and needed when loading */
	key?: number;

	/** operation */
	op: string;

	/** values by which the rule would search */
	values: any[];
}

/**
 * the type for the rules stored in custom view options
 */
export type RulesSchema = {
	/** the condition between rules  */
	attributeRulesConjunction: string;

	/** rule list */
	rules: RuleSchema[];
}

/**
 * Type combining persisted rule with Spec
 */
export type RuleSpec = {
	/** rule info */
	rule: RuleSchema;

	/** spec for rule */
	spec: Spec;
}

/**
 * Type containing rules schema with specs
 */
export type RuntimeRulesSchema = {
	/** indicates if any rule passes */
	matchAny: boolean;

	/** spec for rule */
	ruleSpecs: RuleSpec[];

	/** url of the rule node */
	ruleUrl?: cnl;
}

/* Map for remembering Rule Spec by custom key (or class type) and spec name */
type SpecKey = string;
export type RuleSpecMap = Record<SpecKey, RuleSpec>;

/**
 * apply the filter to the query
 */
export const applyAttributeFilterToQuery = (query: Query, spec: Spec, values: any, matrixPath?: ExtendedPath, onApply?: RuleOnApplyFilterFunction): Query | null => {
	const inMatrix = matrixPath && spec.getExtendedPath()
		?.isAlongPath(matrixPath);
	const op = xml.OP_EQUALS;

	if (!matrixPath || inMatrix) {
		const valueQuery = spec.applyFilter(query, op, values, inMatrix);
		onApply?.(valueQuery, spec, op, values);
		return valueQuery;
	}

	return null;
};

/**
 * Apply rule to given query; if matrix path given, then only apply rule relative to the path
 */
export const applyRuleSpecToQuery = (ruleSpec: RuleSpec, query: Query, matrixPath?: ExtendedPath, onApply?: RuleOnApplyFilterFunction): Query => {
	const { rule: { op, values }, spec } = ruleSpec;
	const inMatrix = matrixPath && spec.getExtendedPath()
		?.isAlongPath(matrixPath);

	if (!matrixPath || inMatrix) {
		const valueQuery = spec.applyFilter(query, op, values, inMatrix);
		onApply?.(valueQuery, spec, op, values);

		return valueQuery;
	}

	return null;
};

/**
 * Return query to apply runtime rules; if matrix path given, then only apply rules relative to the path
 */
export const applyRulesToQuery = (query: Query, { matchAny, ruleSpecs }: RuntimeRulesSchema, matrixPath?: ExtendedPath, onApply?: RuleOnApplyFilterFunction): void => {
	const containerQuery: Query = matchAny ? query.Or() : query;

	for (const ruleSpec of ruleSpecs) {
		applyRuleSpecToQuery(ruleSpec, containerQuery, matrixPath, onApply);
	}
};

/**
 * Save Rule Spec to map with optional custom key.
 */
export function addRuleSpecToFilterMap(spec: Spec, value: string[], ruleSpecMap: RuleSpecMap): void {
	if (!value.length) {
		return;
	}
	const ruleSpec = {
		rule: {
			op: xml.OP_EQUALS,
			values: value,
		},
		spec,
	};
	const specExtendedKey = spec.key.toString();
	ruleSpecMap[specExtendedKey] = ruleSpec;
}

/**
 * Apply rules to an original query from a map that saves the RuleSpecs by custom key and spec name.
 */
export function applyRulesToQueryFromRuleSpecFilterMap(
	originalQuery: Query,
	ruleSpecMap: RuleSpecMap,
): Query {
	const q = new Query();
	q.importQuery(originalQuery);
	for (const extendedKey in ruleSpecMap) {
		applyRuleSpecToQuery(ruleSpecMap[extendedKey], q);
	}
	return q;
}

/**
 * Get value from map created above using key and spec name.
 */
export function getValuesFromRuleSpecFilterMap(spec: Spec, ruleSpecMap: RuleSpecMap): string[] | [] {
	const specExtendedKey = spec.key.toString();
	return ruleSpecMap?.[specExtendedKey]?.rule.values || [];
}

/**
 * Parse rule (custom view) node to get configured rules; matrixPath is provided if some attributes are along a 0..* path
 */
export const getRuntimeRulesFromNode = (ruleNode: C8Node, matrixPath?: ExtendedPath): RuntimeRulesSchema => {
	const { Keys, Options } = ruleNode;
	const ruleUrl = ruleNode.$URL;
	const { attributeRulesConjunction, rules } = JSON.parse(Options) as RulesSchema;
	const matchAny = attributeRulesConjunction === "OR";
	const startClass = matrixPath?.startClass;
	const n = matrixPath?.depth;

	const ruleSpecs: RuleSpec[] = [];

	for (const rule of rules) {
		const keyObj = new Key({ dpkey: Keys[rule.key] });
		const spec = specBuilder.getSpecForKey(keyObj);

		// the attribute could be custom one, so could be null, exclude it here
		if (!spec) {
			continue;
		}

		const { extendedPath } = keyObj;

		if (matrixPath && extendedPath.isAlongPath(matrixPath)) {
			const relativePath = ExtendedPath.create(startClass, spec.querySpec.path);
			relativePath.shiftSteps(n);
			spec.matrixExtendedPath = matrixPath;
			spec.querySpec.pathInMatrix = relativePath.getQueryPath();
		}

		ruleSpecs.push({ rule, spec });
	}

	return { matchAny, ruleSpecs, ruleUrl };
};

/**
 * Return query to apply runtime rules; if matrix path given, then only apply rules relative to the path
 */
export const getRuntimeRulesQuery = (runtimeRules: RuntimeRulesSchema, matrixPath?: ExtendedPath, onApply?: RuleOnApplyFilterFunction): Query | null => {
	if (runtimeRules) {
		const query = new Query();
		applyRulesToQuery(query, runtimeRules, matrixPath, onApply);
		return query;
	}

	return null;
};

/**
 * Convert rule values to formatted string for summary
 */
export const getRuleSpecValueString = (ruleSpec: RuleSpec): string => {
	const { rule: { values }, spec } = ruleSpec;
	return values.map((value) => csiHeading.getTextForFilterValue(null, spec, value === "" ? xml.VALUE_BLANK : value))
		.join(", ");
};

/**
 * Get the specs based on the given keys; if matrix path is provided, then that acts as a prefix to all keys
 */
export function getSpecsForKeys(keys: Key[], matrixPath?: ExtendedPath): Spec[] {
	return _.compact(keys.map((key) => getSpecForKey(key, matrixPath)));
}

/**
 * Get the spec based on the given key
 */
export function getSpecForKey(key: Key, matrixPath?: ExtendedPath): Spec | null {
	let spec: Spec;

	if (matrixPath) {
		const startClass = matrixPath.startClass;
		const n = matrixPath.depth;

		const matrixKey = key.getKeyAlongPath(matrixPath);
		const spec = specBuilder.getSpecForKey(matrixKey);

		if (!spec) {
			return null;
		}

		spec.matrixExtendedPath = matrixPath;

		// for "image" spec, the querySpec doesn't exist
		if (spec.querySpec) {
			const relativePath = ExtendedPath.create(startClass, spec.querySpec.path);
			relativePath.shiftSteps(n);
			spec.querySpec.pathInMatrix = relativePath.getQueryPath();
		}

		return spec;
	} else {
		spec = specBuilder.getSpecForKey(key);
	}

	// the spec would be null in the case where this particular attribute get deleted
	return spec?.isAvailable ? spec : null;
}

/** extract the key map and the rules based on the rule store */
export function getKeyMapAndRules(ruleStore: RuleStore): [Record<number, string>, string] {
	const { ruleMap, selectedCondition } = ruleStore;

	// collect rules
	const keyMap: Record<number, string> = {};
	const rules: RuleSchema[] = [];

	for (const [index, { attributeKey, selectedOperator, selectedValues }] of ruleStore.rules.entries()) {
		if (_.isEmpty(selectedValues)) {
			continue;
		}

		keyMap[index] = attributeKey.toString();
		rules.push({
			key: index,
			op: selectedOperator.id,
			values: selectedValues?.map((item) => item.value),
		});
	}

	// compose the filters and convent to JSON
	const attributeRulesConjunction = conjunctionMap[selectedCondition.id];
	const attributeRules: RulesSchema = {
		attributeRulesConjunction,
		rules,
	};
	const rulesJSON = JSON.stringify(attributeRules);

	return [keyMap, rulesJSON];
}

/**
 * Update rules based on the store
 */
export const updateRules = (ruleUrl: cnl, writer: Writer, ruleStore: RuleStore): void => {
	// extract the key and rules
	const [keyMap, rulesJSON] = getKeyMapAndRules(ruleStore);

	//  update the attributes
	writer.changeNode(ruleUrl)
		.changeAttribute("Keys", xml.ATTR_STRINGMAP, keyMap)
		.changeAttribute("Options", xml.ATTR_STRING, rulesJSON);
};